import { HStack, SimpleGrid, Stack, Text } from '@chakra-ui/react';
import { join } from '@pelicargo/utils';
import { useCallback, useMemo } from 'react';

import { trpc } from '../../config/trpc';
import { useAuth } from '../../context/AuthProvider';
import { CONTACT_TAGS } from '../../utils/constants';
import { AirlineInput } from '../Autocomplete/AirlineInput';
import { AirportInput } from '../Autocomplete/AirportInput';
import { SimpleCheckbox } from '../SimpleCheckbox';
import { SimpleField } from '../SimpleField';
import { SimpleFieldArray } from '../SimpleFieldArray';
import { SimpleInput } from '../SimpleInput';
import { SimpleReactSelect } from '../SimpleReactSelect';
import { SimpleTextarea } from '../SimpleTextarea';

export const ContactForm = () => {
  const { knownTenants } = useAuth();
  const { data: accounts } = trpc.getAllAccounts.useQuery();

  const accountOptions = useMemo(() => {
    return (
      accounts?.map((account) => ({
        value: String(account?.id),
        label: `${account?.name} (${String(account?.id)})`,
      })) ?? []
    );
  }, [accounts]);

  const getAssociationTitle = useCallback(
    (association: any) => {
      const providers = association?.providers?.length ? join(association?.providers) : '-';

      const airports = association?.airports?.length ? join(association?.airports) : '-';
      const accounts = association?.accounts?.length
        ? join(
            association?.accounts?.map(
              (accountId) =>
                accountOptions?.find((accountOption) => Number(accountOption.value) === Number(accountId))?.label,
            ),
          )
        : '-';
      const tags = association?.tags?.length ? join(association.tags) : '-';

      // for some reason new lines aren't respected
      return (
        <Text display="inline">
          Airlines: {providers} Airports: {airports} Accounts: {accounts} Tags: {tags}
        </Text>
      );
    },
    [accountOptions],
  );

  const tagsOptions = useMemo(() => {
    return CONTACT_TAGS.map((option) => {
      return { label: option, value: option };
    });
  }, []);

  return (
    <Stack direction="column" spacing={4} w="full">
      <Stack direction={{ base: 'column', md: 'row' }} w="full">
        <SimpleInput name="full_name" label="Full Name" placeholder="Someone" isRequired />
        <SimpleInput name="preferred_name" label="Preferred Name" placeholder="Awesome" isRequired />
      </Stack>
      <SimpleInput name="title" label="Title" />
      <SimpleInput name="email" label="Email" placeholder="someone@awesomecompany.com" isRequired />

      <SimpleInput name="phone_number" label="Phone" placeholder="Call me maybe?" />

      <SimpleTextarea label="Notes" placeholder="Notes" name="notes" rows={8} />
      <HStack alignItems="end">
        <SimpleReactSelect
          name="tenants"
          label="Tenants"
          options={knownTenants.map((t) => ({
            label: t,
            value: t,
          }))}
          isMulti
          menuPlacement="top"
        />
        <SimpleCheckbox name="is_impersonation_contact" label="Is contact for external tenant use" />
      </HStack>
      <SimpleFieldArray
        name="associations"
        addLabel="Add Association"
        accordionTitle={getAssociationTitle}
        ignoreStyles
        form={({ prefix }) => {
          return (
            <SimpleGrid columns={2} spacing={2}>
              <SimpleField name="providers" label="Airlines">
                <AirlineInput name={`${prefix}.providers`} placeholder="Airlines" isMulti menuPlacement="top" />
              </SimpleField>
              <SimpleField name="airports" label="Airports">
                <AirportInput name={`${prefix}.airports`} placeholder="Airports" isMulti menuPlacement="top" />
              </SimpleField>

              <SimpleReactSelect
                name={`${prefix}.tags`}
                label="Tags"
                type="creatable"
                options={tagsOptions}
                isMulti
                menuPlacement="top"
              />
              <SimpleReactSelect
                name={`${prefix}.accounts`}
                label="Accounts"
                options={accountOptions}
                isMulti
                menuPlacement="top"
              />
            </SimpleGrid>
          );
        }}
        atLeastOneFieldOpen
      />
    </Stack>
  );
};
